/**
 * Real-User Monitoring for Web Vitals (LCP, CLS, INP, FCP, TTFB).
 *
 * - Uses the `web-vitals` library's "v3 attribution" entries so we know
 *   *which* element caused a poor LCP / layout shift / interaction.
 * - Buffers metrics and flushes via `navigator.sendBeacon` on
 *   `visibilitychange === "hidden"` (works during page unload, tab switch,
 *   and bfcache eviction).
 * - Best-effort writes into the existing `user_analytics` table when a
 *   Supabase session is present. Anonymous traffic still surfaces in
 *   `window.__WEB_VITALS__` and the console for ad-hoc debugging.
 * - Sampling can be tuned via `VITE_WEB_VITALS_SAMPLE` (0–1, default 1).
 */
import { onCLS, onINP, onLCP, onFCP, onTTFB, type Metric } from "web-vitals/attribution";
import { supabase } from "@/integrations/supabase/client";

type StoredMetric = {
  name: Metric["name"];
  value: number;
  rating: Metric["rating"];
  id: string;
  navigationType: Metric["navigationType"];
  // Attribution detail (e.g. element selector, event target). Truncated.
  attribution?: Record<string, unknown>;
  page: string;
  ts: number;
};

declare global {
  interface Window {
    __WEB_VITALS__?: StoredMetric[];
  }
}

const SAMPLE = Number(import.meta.env.VITE_WEB_VITALS_SAMPLE ?? "1");
const buffer: StoredMetric[] = [];
let flushed = false;

function summarizeAttribution(metric: Metric): Record<string, unknown> | undefined {
  const attr = (metric as any).attribution;
  if (!attr || typeof attr !== "object") return undefined;
  // Keep payload tiny — drop large DOM refs / event objects.
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(attr)) {
    if (v == null) continue;
    if (typeof v === "string") out[k] = v.slice(0, 200);
    else if (typeof v === "number" || typeof v === "boolean") out[k] = v;
  }
  return out;
}

function record(metric: Metric) {
  const entry: StoredMetric = {
    name: metric.name,
    value: Math.round(metric.value * 1000) / 1000,
    rating: metric.rating,
    id: metric.id,
    navigationType: metric.navigationType,
    attribution: summarizeAttribution(metric),
    page: window.location.pathname,
    ts: Date.now(),
  };
  buffer.push(entry);
  (window.__WEB_VITALS__ ||= []).push(entry);
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.log(`[web-vitals] ${entry.name} = ${entry.value} (${entry.rating})`, entry.attribution ?? "");
  }
}

async function flush() {
  if (flushed || buffer.length === 0) return;
  flushed = true;
  const batch = buffer.splice(0, buffer.length);

  try {
    const { data } = await supabase.auth.getSession();
    const userId = data?.session?.user?.id;
    if (!userId) return; // Anonymous: keep in window only.
    // One row per metric — keeps the existing user_analytics shape simple.
    const rows = batch.map((m) => ({
      user_id: userId,
      event_type: "web_vital",
      event_data: m as any,
      page_path: m.page,
    }));
    await supabase.from("user_analytics").insert(rows);
  } catch (err) {
    if (import.meta.env.DEV) console.warn("[web-vitals] flush failed", err);
  } finally {
    flushed = false; // Allow flushing again if more metrics arrive.
  }
}

export function initWebVitals() {
  if (typeof window === "undefined") return;
  if ((window as any).__WEB_VITALS_INITIALIZED__) return;
  (window as any).__WEB_VITALS_INITIALIZED__ = true;

  // Respect sampling — pick once per page load so all metrics from the same
  // session are either all kept or all dropped.
  if (Number.isFinite(SAMPLE) && Math.random() > SAMPLE) return;

  onLCP(record);
  onCLS(record);
  onINP(record);
  onFCP(record);
  onTTFB(record);

  // Flush on backgrounding (most reliable signal). Also try on pagehide.
  const tryFlush = () => {
    if (document.visibilityState === "hidden") void flush();
  };
  document.addEventListener("visibilitychange", tryFlush, { capture: true });
  window.addEventListener("pagehide", () => void flush(), { capture: true });
}
